import useAuthService from 'api/services/authService'
import useBugReportService from 'api/services/bugReport'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { toast } from 'react-toastify'
import { CustomColorsRef, IconSizes } from 'types/general'

import Button from './common/Button'
import { TextareaInput } from './common/field'
import IconButton from './common/iconButton/IconButton'
import Tooltip from './common/Tooltip'

const MAX_CHARACTERS = 500

interface BugReportResponse {
  success: boolean
  message?: string
}

const BugReport = ({ mobile = false }: { mobile?: boolean }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isValid, setIsValid] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const { submitBugReport } = useBugReportService()
  const { getUserCredentials } = useAuthService()
  const user = getUserCredentials()
  const { t } = useTranslation()

  const initialFormData = {
    email: user?.email || '',
    name: user?.name || '',
    url: window.location.href,
    message: '',
  }

  const [formData, setFormData] = useState(initialFormData)

  const { mutateAsync: submitBugReportMutation, isLoading } = useMutation<
    BugReportResponse,
    Error,
    any
  >(submitBugReport)

  const toggleForm = () => setIsOpen(!isOpen)

  const handleSubmit = async () => {
    if (formData.message.trim()) {
      try {
        const response = await submitBugReportMutation(formData)

        if (!response?.success) {
          throw new Error(response?.message || t("common.failedToSubmitBugReport"))
        }

        setFormData(initialFormData)
        setIsOpen(false)

        toast.success(t("common.bugReportSubmittedSuccessfully") as string)
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "We couldn't create your account. Please check your details and try again."

        toast.error(errorMessage)
      }
    }
  }

  return (
    <div>
      <div
        className="relative h-full flex items-center"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="hidden md:block">
          <IconButton
            icon="regular-bug"
            size={IconSizes.LG}
            color={CustomColorsRef.White}
            handleClick={toggleForm}
          />
        </div>
        
        <div className="md:hidden">
          <button
            onClick={toggleForm}
            className="w-full flex items-center space-x-3 px-4 py-3 text-imovWhite rounded-lg text-left transition-all duration-200 outline-none focus:outline-none"
          >
            <span className="font-medium">{t("common.reportBug")}</span>
          </button>
        </div>
        
        <Tooltip
          text={t("common.reportBugDescription")}
          showTooltip={isHovered && !mobile}
        />
      </div>

      {isOpen && (
        <>
          <div className="md:hidden fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold text-red-700">
                    Report a Bug
                  </h2>
                  <button
                    onClick={toggleForm}
                    className="text-gray-500 hover:text-gray-700 text-xl"
                  >
                    ×
                  </button>
                </div>
                <TextareaInput
                  name="bug-report"
                  label={t("common.describeTheIssue")}
                  value={formData.message}
                  setIsValid={setIsValid}
                  onChange={e => {
                    if ('value' in e && typeof e.value === 'string') {
                      setFormData({ ...formData, message: e.value })
                    }
                  }}
                  required
                />
                <div className="flex justify-between items-center mt-4 text-sm text-gray-500">
                  <span>
                    {formData.message.length}/{MAX_CHARACTERS}
                  </span>
                  <Button
                    onClick={handleSubmit}
                    isLoading={isLoading}
                    disabled={!isValid || !formData.message.trim()}
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="hidden md:block absolute mt-3 p-4 bg-white rounded-xl shadow-lg w-80">
            <h2 className="text-md font-semibold mb-2 text-red-700">
              Describe the issue
            </h2>
            <TextareaInput
              name="bug-report"
              label={t("common.describeTheIssue")}
              value={formData.message}
              setIsValid={setIsValid}
              onChange={e => {
                if ('value' in e && typeof e.value === 'string') {
                  setFormData({ ...formData, message: e.value })
                }
              }}
              required
            />
            <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
              <span>
                {formData.message.length}/{MAX_CHARACTERS}
              </span>
              <Button
                onClick={handleSubmit}
                isLoading={isLoading}
                disabled={!isValid || !formData.message.trim()}
              >
                Submit
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default BugReport
